// Tracks ACTIVE time a visitor spends on the page (not wall-clock time).
// - Generates one session_id per pageview.
// - Pauses the clock whenever the tab is hidden / window unfocused / device idle,
//   so a tab left open in the background doesn't inflate the metric.
// - Reports duration via navigator.sendBeacon on visibility-hidden / pagehide
//   and via periodic heartbeats while the tab is visible.

import { API } from "./api";

const ENDPOINT = `${API}/analytics/session`;
const IDLE_AFTER_MS = 60_000;   // pause after 60s without user interaction
const HEARTBEAT_MS = 30_000;    // flush every 30s while active

function rid() {
  return `s_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
}

function send(sessionId, durationSeconds, page) {
  const payload = JSON.stringify({
    session_id: sessionId,
    duration_seconds: Math.round(durationSeconds),
    page,
  });
  try {
    if (navigator.sendBeacon) {
      const blob = new Blob([payload], { type: "application/json" });
      if (navigator.sendBeacon(ENDPOINT, blob)) return;
    }
  } catch (_) { /* fall through */ }
  try {
    fetch(ENDPOINT, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: payload,
      keepalive: true,
    }).catch(() => {});
  } catch (_) { /* swallow */ }
}

export function startSessionTracker(page = "landing") {
  if (typeof window === "undefined") return () => {};

  const sessionId = rid();
  let activeMs = 0;            // accumulated active milliseconds
  let lastTick = Date.now();   // last time we added to activeMs
  let lastActivity = Date.now();
  let isActive = !document.hidden && document.hasFocus();

  const tickActive = () => {
    const now = Date.now();
    if (isActive) {
      const delta = now - lastTick;
      // ignore implausibly large gaps (e.g. system sleep)
      if (delta > 0 && delta < 60_000) activeMs += delta;
    }
    lastTick = now;
  };

  const elapsedSec = () => {
    tickActive();
    return activeMs / 1000;
  };

  const flush = () => send(sessionId, elapsedSec(), page);

  const setActive = (state) => {
    tickActive();           // flush whatever active time we had so far
    isActive = state;
    lastTick = Date.now();
  };

  const onVis = () => {
    if (document.visibilityState === "hidden") {
      setActive(false);
      flush();
    } else {
      setActive(true);
      lastActivity = Date.now();
    }
  };

  const onFocus = () => { setActive(true); lastActivity = Date.now(); };
  const onBlur = () => { setActive(false); };
  const onActivity = () => {
    lastActivity = Date.now();
    if (!isActive && !document.hidden) setActive(true);
  };
  const onHide = () => { setActive(false); flush(); };

  document.addEventListener("visibilitychange", onVis);
  window.addEventListener("focus", onFocus);
  window.addEventListener("blur", onBlur);
  window.addEventListener("pagehide", onHide);
  ["mousemove", "keydown", "scroll", "touchstart", "click"].forEach(
    (ev) => window.addEventListener(ev, onActivity, { passive: true })
  );

  // Idle watcher: if user does nothing for IDLE_AFTER_MS, pause counting.
  const idleWatch = setInterval(() => {
    if (isActive && Date.now() - lastActivity > IDLE_AFTER_MS) {
      setActive(false);
    }
  }, 10_000);

  // Heartbeat: persist current active time periodically.
  const hb = setInterval(flush, HEARTBEAT_MS);

  // Early ping so 5-sec bounces are still captured.
  const earlyPing = setTimeout(flush, 5_000);

  return () => {
    clearTimeout(earlyPing);
    clearInterval(hb);
    clearInterval(idleWatch);
    document.removeEventListener("visibilitychange", onVis);
    window.removeEventListener("focus", onFocus);
    window.removeEventListener("blur", onBlur);
    window.removeEventListener("pagehide", onHide);
    ["mousemove", "keydown", "scroll", "touchstart", "click"].forEach(
      (ev) => window.removeEventListener(ev, onActivity)
    );
    flush();
  };
}
